import { generatePath } from 'react-router-dom';

import { isMobile } from 'hooks';
import { ButtonColors } from 'types';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { List } from 'components/list';
import { Config, AppRoutes } from 'config';
import { Avatar } from 'components/avatar';
import { Button } from 'components/button';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { InfoBlock } from 'components/info-block';
import { ListHeader } from 'components/list-header';
import { RefundStatusBadge } from 'components/refund-status-badge';

import { getRefundFields } from './config';
import { RefundDetailsBlockProps } from './types';

const template = 'repeat(4, 1fr) 128px';
const tabletTemplate = '2fr repeat(4, 1fr)';
const titles = ['Product', 'Price', 'Quantity', 'Total', 'Damaged'];
const cellsMobile = [
  { row: '1/2', col: '1/3' },
  { row: '2/3', col: '1/2' },
  { row: '2/3', col: '2/3' },
  { row: '3/4', col: '1/2' },
];

export const RefundInfoBlock = ({ data }: RefundDetailsBlockProps) => {
  const mobile = isMobile();

  if (!data?.refund?.length) {
    return null;
  }

  return data?.refund?.map((value) => (
    <InfoBlock
      key={value.id}
      title={`Refund #${value.id}`}
      rightSlot={<RefundStatusBadge status={value.status} />}
    >
      <Flex column gap={16}>
        <Flex column gap={8}>
          {getRefundFields(value).map((field) => (
            <p key={field.title}>
              <b>{field.title}:</b> {field.value}
            </p>
          ))}
        </Flex>

        <List
          count={data.items.length}
          mainWrapperStyles={{ padding: 0 }}
          header={
            <ListHeader padding="8px" titles={titles} template={template} />
          }
        >
          {value?.refundItems?.map((item, index) => (
            <ListCard
              headers={titles}
              template={template}
              cellsMobile={cellsMobile}
              key={`${item.id}-${index}`}
              tabletTemplate={tabletTemplate}
              values={[
                <Flex gap={8} alignItems="center">
                  <Avatar url={item?.checkoutItem?.product?.photoUrl} />
                  <h4>{item?.checkoutItem?.product?.name}</h4>
                </Flex>,
                <b>{formatPrice(item?.price)}</b>,
                <b>{item?.quantity}</b>,
                <b>{formatPrice(item?.price * item?.quantity)}</b>,
                <b>{item.isDamaged ? 'Yes' : 'No'}</b>,
              ]}
            />
          ))}
        </List>

        <Flex gap={16} column={mobile}>
          <Button
            text="Print"
            fullWidth={mobile}
            icon={<Icon.Printer />}
            disabled={!value?.pdfUrl}
            colorType={ButtonColors.LightBlue}
            onClick={() => window.open(value?.pdfUrl, '_blank')}
          />

          {Config.isDebugHTMLEnabled && (
            <Button
              text="View HTML"
              fullWidth={mobile}
              to={generatePath(AppRoutes.Products.Receipts.RefundHTML, {
                refundId: value.id,
              })}
            />
          )}
        </Flex>
      </Flex>
    </InfoBlock>
  ));
};
